import { isEmpty } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useContent } from 'src/contexts/ContentContext';
import { ContentId } from 'src/utils/constants/contentId';
import { GroupingType } from 'src/WebApiController';
import { ListingGroup, ListingGroupItemInput } from 'src/WebApiController';

import { MergeListingGroupInputListFields } from './components/groupingTypes';
import {
  flattenListingGroup,
  sortListingsBySortId,
} from './components/groupingUtils';
import { GroupListingsModalContent } from './GroupListings';

export const AddToGroupListingsModal = ({
  selectedListingIds,
  listingGroupId,
  viagVirtId,
  newListingGroupName,
  inheritListingGroupSettings,
  maintainPriceStagger,
  defaultDesiredActiveListings,
  defaultPricingSettingsJson,
  defaultDeprioritizedQuantities,
  defaultSortId,
  onGroupCreated,
}: {
  selectedListingIds: number[];
  listingGroupId?: string | undefined;
  viagVirtId: string;
  newListingGroupName?: string;
  inheritListingGroupSettings?: boolean;
  maintainPriceStagger?: boolean;
  /** When creating a new group, override the default desired active listings with this account default. */
  defaultDesiredActiveListings?: number;
  /** When creating a new group, the account-default pricing settings JSON (rank premium). */
  defaultPricingSettingsJson?: string | null;
  /** When creating a new group, the account-default deprioritized quantities (comma-separated). */
  defaultDeprioritizedQuantities?: string | null;
  /** When creating a new group, the account-default sort ID for initial listing priority order. */
  defaultSortId?: number | null;
  onGroupCreated?: (groupId: string) => void;
}) => {
  const { eventsTransformed } = useCatalogDataContext();

  const eventData = useMemo(() => {
    return eventsTransformed?.find((e) => e.event.virtId === viagVirtId);
  }, [eventsTransformed, viagVirtId]);

  const listingsToAdd = useMemo(() => {
    return eventData?.entities?.listings
      ?.flatMap(flattenListingGroup)
      .filter((l) => selectedListingIds.includes(l.id));
  }, [eventData, selectedListingIds]);

  const listingGroup = useMemo(() => {
    if (!isEmpty(newListingGroupName)) {
      // !important don't assign ltGrpId, as this is the indicator of when this is a new listing group
      // But we need ths isLtGrp, which is used to flatten the listings from the group
      return {
        groupName: newListingGroupName,
        isLtGrp: true,
        desiredActiveListings: null,
        minActiveListings: null,
        maxActiveListings: null,
        targetPercentage: null,
        deprioritizedQuantities: null,
        marketplaceSettings: null,
        absoluteRankPremium: null,
        relativeRankPremium: null,
      } as ListingGroup;
    }
    return eventData?.entities?.listings?.find(
      (l) => l.isLtGrp && l.ltGrpId === listingGroupId
    ) as ListingGroup;
  }, [eventData?.entities?.listings, listingGroupId, newListingGroupName]);

  const event = eventData?.event;

  // Determines if we are creating a new group (no existing group ID)
  const isNewGroup = !listingGroup?.ltGrpId;

  const listings = useMemo(() => {
    return [...flattenListingGroup(listingGroup), ...(listingsToAdd ?? [])];
  }, [listingGroup, listingsToAdd]);

  // For new groups, sort listings by the account-default sort order before assigning priorities
  const sortedListings = useMemo(() => {
    if (!isNewGroup || defaultSortId == null) return listings;
    return sortListingsBySortId(listings, defaultSortId);
  }, [listings, isNewGroup, defaultSortId]);

  // For new groups, parse the account-default pricing settings JSON into rank premium values
  const accountPricingSettings = useMemo(() => {
    if (!isNewGroup || !defaultPricingSettingsJson) return null;
    try {
      return JSON.parse(defaultPricingSettingsJson) as {
        undAbsAmt: number | null;
        undRelAmt: number | null;
      };
    } catch {
      return null;
    }
  }, [isNewGroup, defaultPricingSettingsJson]);

  // For new groups, parse the account-default deprioritized quantities from comma-separated string
  const accountDeprioritizedQuantities = useMemo(() => {
    if (!isNewGroup || !defaultDeprioritizedQuantities) return null;
    return defaultDeprioritizedQuantities.split(',').filter(Boolean);
  }, [isNewGroup, defaultDeprioritizedQuantities]);

  const newGroupName = useContent(ContentId.NewGroup);
  const methods = useForm<MergeListingGroupInputListFields>({
    defaultValues: {
      mergeListingGroupInputs: [
        {
          listingGroupId: listingGroup?.ltGrpId ?? listingGroupId,
          name: listingGroup?.groupName ?? `${newGroupName} 1`,
          listingGroupItems: sortedListings.map(
            (l, idx) =>
              ({
                listingId: l.id,
                priority: l.ltGrpPrior ?? idx + 1,
                groupId: l.ltGrpId,
                undercutSetting: l.ltGrpUndercut,
              }) as ListingGroupItemInput
          ),
          viagogoEventId: event?.viagId,
          viagogoMappingId: event?.mpId,
          desiredActiveListings:
            listingGroup?.desiredActiveListings ??
            defaultDesiredActiveListings ??
            1,
          minActiveListings: listingGroup?.minActiveListings,
          maxActiveListings: listingGroup?.maxActiveListings,
          targetPercentage: listingGroup?.targetPercentage,
          deprioritizedQuantities:
            listingGroup?.deprioritizedQuantities ??
            accountDeprioritizedQuantities ??
            [],
          marketplaceSettings: listingGroup?.marketplaceSettings,
          inheritListingGroupSettings,
          maintainPriceStagger,
          groupUndercutSetting: {
            undAbsAmt:
              listingGroup?.absoluteRankPremium ??
              accountPricingSettings?.undAbsAmt ??
              null,
            undRelAmt:
              listingGroup?.relativeRankPremium ??
              accountPricingSettings?.undRelAmt ??
              null,
            actRankUndAbsAmt: null,
            actRankUndRelAmt: null,
          },
        },
      ],
      templateSettings: {
        groupBy: {
          groupingType: GroupingType.Custom,
        },
        desiredActiveListings: 0,
        deprioritizedQuantities: [],
      },
      eventSettings: [],
    },
  });

  const { reset } = methods;

  useEffect(() => {
    reset({
      mergeListingGroupInputs: [
        {
          name: listingGroup?.groupName ?? `${newGroupName} 1`,
          listingGroupId: listingGroup?.ltGrpId ?? listingGroupId,
          listingGroupItems: sortedListings.map(
            (l, idx) =>
              ({
                listingId: l.id,
                priority: l.ltGrpPrior ?? idx + 1,
                groupId: l.ltGrpId,
                undercutSetting: l.ltGrpUndercut,
              }) as ListingGroupItemInput
          ),
          viagogoEventId: event?.viagId,
          viagogoMappingId: event?.mpId,
          desiredActiveListings:
            listingGroup?.desiredActiveListings ??
            defaultDesiredActiveListings ??
            1,
          minActiveListings: listingGroup?.minActiveListings,
          maxActiveListings: listingGroup?.maxActiveListings,
          targetPercentage: listingGroup?.targetPercentage,
          deprioritizedQuantities:
            listingGroup?.deprioritizedQuantities ??
            accountDeprioritizedQuantities ??
            [],
          marketplaceSettings: listingGroup?.marketplaceSettings,
          inheritListingGroupSettings,
          maintainPriceStagger,
          groupUndercutSetting: {
            undAbsAmt:
              listingGroup?.absoluteRankPremium ??
              accountPricingSettings?.undAbsAmt ??
              null,
            undRelAmt:
              listingGroup?.relativeRankPremium ??
              accountPricingSettings?.undRelAmt ??
              null,
            actRankUndAbsAmt: null,
            actRankUndRelAmt: null,
          },
        },
      ],
      templateSettings: {
        groupBy: {
          groupingType: GroupingType.Custom,
        },
        desiredActiveListings: 0,
        deprioritizedQuantities: [],
      },
      eventSettings: [],
    });
  }, [
    sortedListings,
    listingGroup,
    event,
    newGroupName,
    reset,
    inheritListingGroupSettings,
    maintainPriceStagger,
    listingGroupId,
    defaultDesiredActiveListings,
    accountDeprioritizedQuantities,
    accountPricingSettings,
  ]);
  return (
    <FormProvider {...methods}>
      <GroupListingsModalContent
        {...methods}
        events={event ? [event] : undefined}
        listingGroup={listingGroup}
        onGroupCreated={onGroupCreated}
      />
    </FormProvider>
  );
};
